/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import React from 'react';

const QueueStatusInner = ({ pmStats }: any) => {
    const theme : any = useTheme();
    const navigate = useNavigate();

    let cx = 0;
    let cy = 20;
    let out = [ ];
    const statAreaWidth = 200;
    if (!pmStats || !pmStats.topn) {
        return <div>Pending</div>;
    }
    // Manager status
    let statBlock = (key, name, val) => {
        let v = (
            <g key={key}>
                <text x={cx} y={cy} stroke={theme.colors.ctext} fontFamily="Monospace" fontSize="12px">
                    <tspan textAnchor="end" x={statAreaWidth/2}>{name}:</tspan>
                    <tspan textAnchor="start" x={statAreaWidth/2 + 3 } stroke="cyan">{val}</tspan>
                </text>
            </g>
        );
        cy += 20;
        return v;
    };
    out.push(statBlock('execState', 'State', pmStats.execState));
    out.push(statBlock('runningProbes', 'Running', pmStats.runningProbes.length.toString()));
    out.push(statBlock('runningJobs', 'Jobs Run', String(pmStats.runningQueueCounts?.jobs ?? 0)));
    out.push(statBlock('runningTasks', 'Tasks Run', String(pmStats.runningQueueCounts?.tasks ?? 0)));
    out.push(statBlock('lastCmd', 'Last Cmd', pmStats.lastCmd));
    out.push(statBlock('heartBeat', 'Heartbeat', Number(pmStats.heartBeat).toString(16)));
    out.push(statBlock('pending', 'Pending', pmStats.topn.counts.pending));
    out.push(statBlock('pendingJobs', 'Jobs Q', String(pmStats.topn.counts.pending_jobs ?? 0)));
    out.push(statBlock('pendingTasks', 'Tasks Q', String(pmStats.topn.counts.pending_tasks ?? 0)));
    out.push(statBlock('completed', 'Completed', pmStats.topn.counts.completed));


    let arrow = (key) => { return [
    <path key={key} fill={theme.colors.bg} stroke={theme.colors.ctext} 
        d="M 46.5 0.67 L 46.5 9.33 L 22.34 9.33 L 22.35 21.06 L 35.38 21.05 L 18.03 38.5 L 0.65 21.07 L 13.68 21.06 L 13.66 0.67 Z" 
    />,
    <path key={key+"_1"} fill={theme.colors.bg} stroke={theme.colors.ctext} 
        d="M 22.35 21.06 L 35.38 21.05 L 18.03 38.5 L 0.65 21.07 L 13.68 21.06"
    />
    ]};

    // annotations/arrows
    out.push((
        <g transform={`scale(.9,.9) translate(${statAreaWidth+42}, 18)`} key="arrow1">
            {arrow("arrow1")}
        </g>
    ));
    out.push((
        <g transform={`scale(.9,.9) translate(${statAreaWidth+52}, 165) rotate(-90)`} key="arrow2">
            {arrow("arrow2")}
        </g>
    ));

    // Now show the top of the queues
    cx = statAreaWidth;
    cy = 10;
    let xSize = 70;
    let ySize = 40;
    let yPad = 10;
    let xPad = 10;
    let yStep = ySize + yPad;
    let xStep = xSize + xPad;
    let box = (probe, dashes = false) => {
        return (
            <g key={probe.probe_id} id={probe.probe_id}
                onClick={() => {
                    switch (probe.probe_id) {
                        case 'Queue':
                        case 'Executor':
                            break;
                        default:
                            // Navigate to the probe detail page
                            navigate(`/probe/${probe.probe_id}`);
                    }
                }}
            >
                <rect x={cx} y={cy} width={xSize} height={ySize}
                        key={probe.probe_id + "_box"}
                        fill={theme.colors.bg} 
                        stroke={pmStats.runningProbes.some(p => p.probe_id == probe.probe_id) ? "red" : 
                                probe.probe_id == pmStats.priorityProbeId ? "violet" :
                                  theme.colors.ctext}
                        strokeDasharray={dashes ? '5,5' : '0'}
                />
                <text x={cx+xSize/2} y={cy+ySize/2} stroke={theme.colors.ctext} fontFamily="Monospace" fontSize="12px" textAnchor="middle"
                    key={probe.probe_id + "_text"}>
                    <tspan x={cx+xSize/2} dy="-.30em">{probe.probe_id}</tspan>
                    <tspan x={cx+xSize/2} dy="1.4em">{probe.probe_type}</tspan>
                </text>
            </g>
        );
    };

    cx = statAreaWidth + xStep;
    let q = [...pmStats.topn.pending];
    for (let qe of q) {
        if (pmStats.runningProbes.some(p => p.probe_id === qe.probe_id)) {
            q.splice(q.indexOf(qe), 1); // remove the running probe from the pending list
            break;
        }
    }

    if (q.length === 0 && !pmStats.priorityProbeId) {
        out.push(box({ probe_id: 'Queue', probe_type: 'Empty' }, true));
    } else {
        if (pmStats.priorityProbeId) {
            // If there's a priority probe, show it at the front of the queue
            out.push(box({ probe_id: pmStats.priorityProbeId, probe_type: pmStats.priorityProbeType }, false));
            cx += xStep;
        }
        out = out.concat(q.map((probe, index) => {
            const rc = box(probe);
            cx += xStep;
            return rc;
        }));
    }

    cy += yStep;
    cx = statAreaWidth;
    if (pmStats.runningProbes.length === 0) {
        out.push(box({ probe_id: 'Executor', probe_type: 'Idle' }, true));
    } else {
        pmStats.runningProbes.forEach(p1 => {
            out.push(box({ probe_id: p1.probe_id, probe_type: p1.probe_type }, false));
            cx += xStep;
        });
    }

    cy += yStep;
    cx = statAreaWidth + xStep;
    out = out.concat(pmStats.topn.completed.map((probe, index) => {
        if (pmStats.runningProbes.some(p => p.probe_id === probe.probe_id)) {
            return null;
        }
        const rc = box(probe);
        cx += xStep;
        return rc;
    }));
    return (
        <svg width="100%" height="100%">
            <g>
                {out}
            </g>    
        </svg>
    );
}

export default React.memo(QueueStatusInner);